import { useDepot } from '#~/depot/hooks.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import { PostHogManager } from '#~/posthog/index.js';
import { getOrganizationSummaries, getStudioSummaries } from '#~/posthog/utils/summaries.js';
import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';
import type { User } from '@studio/models/user';
import { useEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';

type DepotSlice = [Record<string, Organization>, Record<string, RoomRecord>, User | undefined];

/**
 * Syncs organization group metadata with PostHog via `groupIdentify`.
 *
 * Calls `manager.group('org', ...)` for each org whenever org data changes.
 * That updates each organization's profile only. The manager never leaves an
 * organization as the SDK's active group, so the order of this loop cannot
 * decide which organization an event counts toward; each event's organization
 * comes from its own action (posthog/organization-attribution.ts).
 */
export const useOrgGroupIdentify = (): void => {
  const [organizations, roomRecords, user]: DepotSlice = useDepot(
    (µ) => [µ.organizations, µ.roomRecords, µ.user],
    shallow
  );

  const studioSummaries = useMemo(() => getStudioSummaries(roomRecords), [roomRecords]);
  const orgSummaries = useMemo(
    () => getOrganizationSummaries(organizations, studioSummaries),
    [organizations, studioSummaries]
  );

  const signature = useMemo(() => {
    if (!user) {
      return null;
    }

    return JSON.stringify(
      orgSummaries.map(({ id, name, industry, website, studioCount }) => ({
        id,
        name,
        industry,
        website,
        studioCount
      }))
    );
  }, [orgSummaries, user]);

  const lastSignature = useRef<string | null>(null);

  useEffect(() => {
    if (isElectronTrayWindow() || !user || !signature || signature === lastSignature.current) {
      if (!user) {
        lastSignature.current = null;
      }

      return;
    }

    lastSignature.current = signature;

    const manager = PostHogManager.getInstance();

    orgSummaries.forEach((org) => {
      manager.group('org', org.id, {
        name: org.name,
        industry: org.industry,
        website: org.website,
        studioCount: org.studioCount
      });
    });
  }, [orgSummaries, signature, user]);
};
